// src/component/admin/support/SupportTicketsTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  SearchIcon,
  ArrowDown,
  ClockIcon,
  CheckCircleIcon,
  CautionIcon,
  EmailIcon,
  StarIcon,
  FilterIcon,
  CloseIcon
} from '../../../public/icons';
import Pagination from '../../../component/shared/Pagination';
import adminSupportService from '../../../services/admin/admin-supportService';
import { useToast } from '../../../context/ToastContext';

/* ═══════════════════════════════════════════════════════════════
   Mobile Filter Modal (unchanged API surface, driven by parent state)
   ═══════════════════════════════════════════════════════════════ */
const MobileFiltersModal = ({
  isOpen,
  onClose,
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  subjectFilter,
  setSubjectFilter,
  statusFilters,
  subjectFilters
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-medium text-[#1C2A46]">Filters</h3>
          <button onClick={onClose} className="p-1">
            <CloseIcon size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={16} className="text-[#6B635D]" />
              </div>
              <input
                type="text"
                placeholder="Search by name, email, subject…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2.5 pl-10 pr-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#D4A574] text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <div className="grid grid-cols-2 gap-2">
              {statusFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setStatusFilter(filter.id)}
                  className={`px-3 py-2 text-sm rounded border transition-colors ${
                    statusFilter === filter.id
                      ? 'bg-[#D2A146] text-white border-[#D2A146]'
                      : 'bg-white text-[#1C2A46] border-[#DBDBDB] hover:border-[#D2A146]'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject
            </label>
            <div className="grid grid-cols-2 gap-2">
              {subjectFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setSubjectFilter(filter.id)}
                  className={`px-3 py-2 text-sm rounded border transition-colors ${
                    subjectFilter === filter.id
                      ? 'bg-[#D2A146] text-white border-[#D2A146]'
                      : 'bg-white text-[#1C2A46] border-[#DBDBDB] hover:border-[#D2A146]'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={() => {
              setSearchQuery('');
              setStatusFilter('all');
              setSubjectFilter('all');
              onClose();
            }}
            className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded hover:bg-gray-50 transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-[#D2A146] text-white font-medium rounded hover:bg-[#C49660] transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Mobile Ticket Card
   ═══════════════════════════════════════════════════════════════ */
const MobileTicketCard = ({ ticket, onView }) => {
  const statusCfg = adminSupportService.getStatusBadgeConfig(ticket.status);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="font-medium text-gray-900 text-base">
            {ticket.name}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">{ticket.email}</div>
        </div>
        <span
          className={`flex items-center gap-1.5 px-2 py-1 text-xs leading-5 font-semibold rounded-full ${statusCfg.bgColor} w-fit`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${statusCfg.dotColor}`} />
          <span className={statusCfg.textColor}>{statusCfg.label}</span>
        </span>
      </div>

      <div className="mt-3 space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">Subject:</span>
          <span className="text-sm text-gray-700">
            {ticket.subjectLabel}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">Priority:</span>
          <span className="text-sm text-gray-700 capitalize">
            {ticket.priority}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">Received:</span>
          <span className="text-sm text-gray-500">{ticket.ageLabel}</span>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="text-sm text-gray-700">
          <span className="text-xs text-gray-500 block mb-1">Message:</span>
          <span className="font-normal line-clamp-2">{ticket.snippet}</span>
        </div>
      </div>

     
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════ */
const SupportTicketsTab = () => {
  const { error: showError } = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    inReview: 0,
    replied: 0,
    resolved: 0,
    closed: 0,
    spam: 0
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalPages: 1,
    totalItems: 0
  });

  /* ── Fetch messages ── */
  const fetchMessages = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);

        const params = {
          page,
          limit: pagination.itemsPerPage,
          search: searchQuery || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          subject: subjectFilter !== 'all' ? subjectFilter : undefined,
          sort: '-createdAt'
        };

        const res = await adminSupportService.getMessages(params);

        if (res.success) {
          setMessages(res.data.messages || []);
          setPagination((prev) => ({
            ...prev,
            currentPage: res.data.pagination.page,
            totalPages: res.data.pagination.pages,
            totalItems: res.data.pagination.total
          }));
        } else {
          setMessages([]);
        }
      } catch (err) {
        console.error('Fetch support messages error:', err);
        showError('Failed to load support messages');
        setMessages([]);
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, statusFilter, subjectFilter, pagination.itemsPerPage, showError]
  );

  /* ── Fetch stats ── */
  const fetchStats = useCallback(async () => {
    try {
      const res = await adminSupportService.getStats();
      if (res.success) {
        setStats(res.data);
      }
    } catch (err) {
      console.error('Fetch support stats error:', err);
    }
  }, []);

  /* ── Effects ── */
  useEffect(() => {
    fetchMessages(1);
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchMessages(1);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, subjectFilter]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
    fetchMessages(page);
  };

  const handleViewTicket = (ticket) => {
    // Hook this up to a detail modal later
    console.log('View ticket:', ticket);
  };

  /* ── Filter config ── */
  const statusFilters = [
    { id: 'all', label: 'All status' },
    { id: 'new', label: 'New' },
    { id: 'in_review', label: 'In Review' },
    { id: 'replied', label: 'Replied' },
    { id: 'resolved', label: 'Resolved' },
    { id: 'closed', label: 'Closed' },
    { id: 'spam', label: 'Spam' }
  ];

  const subjectFilters = [
    { id: 'all', label: 'All subjects' },
    { id: 'general', label: 'General' },
    { id: 'support', label: 'Support' },
    { id: 'billing', label: 'Billing' },
    { id: 'feedback', label: 'Feedback' },
    { id: 'complaint', label: 'Complaint' },
    { id: 'other', label: 'Other' }
  ];

  /* ── Stats cards for the top row ── */
  const statCards = [
    {
      label: 'Open Tickets',
      value: stats.new + stats.inReview,
      icon: <CautionIcon color="#A82121" />,
      color: 'bg-[#F4E7E7] text-[#A82121]'
    },
   
  ];

  /* ── Status badge ── */
  const getStatusBadge = (status) => {
    const cfg = adminSupportService.getStatusBadgeConfig(status);
    return (
      <span
        className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs leading-5 font-semibold rounded-full ${cfg.bgColor} w-fit`}
      >
        <span className={`h-2 w-2 rounded-full ${cfg.dotColor}`} />
        <span className={cfg.textColor}>{cfg.label}</span>
        <ArrowDown size={12} className={cfg.textColor} />
      </span>
    );
  };

  return (
    <div className="px-2 sm:px-0">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-sm p-3 sm:p-4 md:p-5 shadow-sm"
          >
            <div className="text-left">
              <div
                className={`flex justify-center items-center rounded-md h-10 w-10 sm:h-12 sm:w-12 ${stat.color}`}
              >
                {stat.icon}
              </div>
              <div>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1C2A46] mt-2 sm:mt-4">
                  {stat.value}
                </p>
                <p className="text-xs sm:text-sm text-[#6B635D] mt-0.5 sm:mt-1">
                  {stat.label}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-sm shadow-sm">
        {/* Header */}
        <div className="mb-4 sm:mb-6 p-4 sm:p-6 border-b border-[#DBDBDB]">
          {/* Desktop */}
          <div className="hidden lg:flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">
              All Tickets
            </h2>

            <div className="flex gap-3 lg:gap-4 items-center">
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <SearchIcon size={18} className="text-[#6B635D]" />
                </div>
                <input
                  type="text"
                  placeholder="Search by name, email, subject…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="p-2 pl-9 pr-3 border w-64 lg:w-80 border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs lg:placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                />
              </div>

              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="p-2 pl-3 pr-7 border w-32 lg:w-36 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-xs lg:text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                >
                  {statusFilters.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <ArrowDown size={14} className="text-[#6B635D]" />
                </div>
              </div>

              <div className="relative">
                <select
                  value={subjectFilter}
                  onChange={(e) => setSubjectFilter(e.target.value)}
                  className="p-2 pl-3 pr-7 border w-36 lg:w-44 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-xs lg:text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                >
                  {subjectFilters.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <ArrowDown size={14} className="text-[#6B635D]" />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile */}
          <div className="lg:hidden">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-medium text-[#0E1628]">
                All Tickets
              </h2>
              <button
                onClick={() => setMobileFiltersOpen(true)}
                className="flex items-center gap-2 px-3 py-2 border border-[#DBDBDB] rounded-sm text-sm"
              >
                <FilterIcon size={14} />
                Filters
              </button>
            </div>

            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={16} className="text-[#6B635D]" />
              </div>
              <input
                type="text"
                placeholder="Search by name, email, subject…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2.5 pl-10 pr-3 border border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
              />
            </div>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-[#DBDBDB]">
            <thead className="bg-[#F3F4F6]">
              <tr>
                {[
                  'Customer',
                  'Subject',
                  'Priority',
                  'Snippet',
                  
                
                ].map((h) => (
                  <th
                    key={h}
                    className={`px-3 sm:px-4 md:px-6 py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider ${
                      h === 'Action' ? 'w-20' : ''
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#DBDBDB]">
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-sm text-gray-500">
                    Loading tickets…
                  </td>
                </tr>
              ) : messages.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-sm text-gray-500">
                    No tickets found
                  </td>
                </tr>
              ) : (
                messages.map((ticket) => (
                  <tr
                    key={ticket._id}
                    className="hover:bg-gray-50 transition-colors duration-150 text-left"
                  >
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4">
                      <div className="flex flex-col">
                        <span className="text-xs sm:text-sm font-normal text-[#0E1628]">
                          {ticket.name}
                        </span>
                        <span className="text-[10px] sm:text-xs font-normal text-[#6B635D]">
                          {ticket.email}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className="text-xs sm:text-sm font-normal text-[#0E1628]">
                        {ticket.subjectLabel || ticket.subject}
                      </span>
                    </td>

                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4">
                      <span className="text-xs sm:text-sm font-normal text-[#6B635D] line-clamp-2">
                        {ticket.message?.slice(0, 100)}
                        {ticket.message?.length > 100 ? '…' : ''}
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className="text-xs sm:text-sm font-normal text-[#8A9099]">
                        {ticket.ageLabel ||
                          adminSupportService.timeAgo(ticket.createdAt)}
                      </span>
                    </td>
                   
                    
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden px-4 pb-4">
          {loading ? (
            <div className="text-center py-10 text-sm text-gray-500">
              Loading tickets…
            </div>
          ) : messages.length > 0 ? (
            messages.map((ticket) => (
              <MobileTicketCard
                key={ticket._id}
                ticket={{
                  ...ticket,
                  subjectLabel:
                    ticket.subjectLabel ||
                    adminSupportService.getSubjectLabel(ticket.subject),
                  snippet:
                    ticket.message?.slice(0, 120) +
                    (ticket.message?.length > 120 ? '…' : ''),
                  ageLabel:
                    ticket.ageLabel ||
                    adminSupportService.timeAgo(ticket.createdAt)
                }}
                onView={handleViewTicket}
              />
            ))
          ) : (
            <div className="text-center py-10">
              <svg
                className="mx-auto h-10 w-10 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="mt-2 text-sm text-gray-500">
                No tickets found matching your criteria
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {messages.length > 0 && pagination.totalPages > 1 && (
          <div className="border-t border-[#DBDBDB] px-4 sm:px-6 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs text-[#6B635D] order-2 sm:order-1">
                Showing {messages.length} out of{' '}
                <span className="font-medium">{pagination.totalItems}</span>{' '}
                tickets
              </div>
              <div className="order-1 sm:order-2 w-full sm:w-auto">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  itemsPerPage={pagination.itemsPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Filters Modal */}
      <MobileFiltersModal
        isOpen={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        subjectFilter={subjectFilter}
        setSubjectFilter={setSubjectFilter}
        statusFilters={statusFilters}
        subjectFilters={subjectFilters}
      />
    </div>
  );
};

export default SupportTicketsTab;